import { useState, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { TacticStatus } from 'basics/enums/maya.enums';
import useGetAllTacticsLazy from 'basics/graphql/queries/getAllTacticsLazy';
import { CepStateType } from 'basics/types/maya.types';
import { minimumAllowedNumberOfTacticsInGroup } from 'components/TacticForm/TacticForm.constants';
import { Tactic, Channel } from 'generated/maya.types';
import cepState from 'states/cep.states';

const useTacticValidation = () => {
  const cepStateValue = useSnapshot(cepState) as CepStateType;
  const [notValidGroups, setNotValidGroups] = useState<Tactic[]>([]);
  const { fetchTactics } = useGetAllTacticsLazy();

  const validateGroupRelations = (): boolean => {
    const notValidEntities: Tactic[] = [];

    const validationStatus: boolean[] = cepState?.tacticGroups.map((group) => {
      const relatedTactics: Tactic[] = cepState?.tactics.filter((tactic) => tactic?.parentTactic?._id === group?._id);
      const isValidGroup = !relatedTactics.length || relatedTactics.length >= minimumAllowedNumberOfTacticsInGroup;

      if (!isValidGroup) {
        notValidEntities.push(group);
      }

      setNotValidGroups(notValidEntities);
      return isValidGroup;
    });

    return !validationStatus.includes(false);
  };

  const validateGroupRelationsLazy = async (cepId: string): Promise<boolean> => {
    const { data, error: errorLazy } = await fetchTactics({
      cep: cepId,
      status: [TacticStatus.EMPTY, TacticStatus.DRAFT, TacticStatus.APPROVED],
    });

    if (!errorLazy && data) {
      const { tactics: tacticsLazyData } = data;
      cepState.tactics = tacticsLazyData?.filter((tactic) => tactic.channel !== Channel.group);
      cepState.tacticGroups = tacticsLazyData?.filter((tactic) => tactic.channel === Channel.group);
    }

    return validateGroupRelations();
  };

  useEffect(() => {
    validateGroupRelations();
  }, [cepStateValue]);

  useEffect(() => {
    if (cepStateValue.cep?._id && (!cepState.tactics.length || !cepState.tacticGroups.length)) {
      validateGroupRelationsLazy(cepStateValue.cep?._id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cepStateValue.cep?._id]);

  const getNumberOfTacticsInGroup = (childTacticId: string): number => {
    const groups = cepStateValue?.tacticGroups;

    if (!groups?.length) {
      return 0;
    }

    const allTactics = cepStateValue?.tactics;
    const currentTactic = allTactics.find((tactic) => tactic._id === childTacticId);
    const tacticsWithSameGroup = allTactics.filter((tactic) => {
      return tactic.parentTactic && tactic.parentTactic?._id === currentTactic?.parentTactic?._id;
    });

    return tacticsWithSameGroup?.length;
  };

  return {
    notValidGroups,
    validateGroupRelations,
    validateGroupRelationsLazy,
    getNumberOfTacticsInGroup,
  };
};

export default useTacticValidation;
